/**
 * composer-tools.ts — Logic thuần của "tiện ích" ở thanh công cụ ô soạn tin trên ĐIỆN THOẠI (chat chi tiết).
 *
 * Màn hẹp + bàn phím ảo không đủ chỗ cho 8–9 nút như máy tính, nên thanh công cụ chỉ ghim vài tiện ích MẶC ĐỊNH
 * (`DEFAULT_PINNED`, tối đa `MAX_PINNED`); mọi tiện ích còn lại nằm trong hộp "+" (Tiện ích). Người dùng tự chọn tiện ích nào được ghim
 * và xếp thứ tự — lưu THEO TÀI KHOẢN, đồng bộ mọi thiết bị (hooks/use-composer-tools.ts ↔ backend `me/ui-prefs.ts`, khoá `composer-tools`).
 * Giao diện: pages/conversations/composer-tools-sheet.tsx.
 */
import {
  BarChart3, BellRing, CreditCard, FolderOpen, IdCard, Image as ImageIcon, Link2, Paperclip, ShoppingBag, Smile, Zap,
  PackagePlus, type LucideIcon,
} from 'lucide-react'
import { i18n } from '@/lib/i18n'

export type ComposerToolId =
  | 'image' | 'file' | 'sticker' | 'library' | 'quick' | 'card' | 'bank' | 'link' | 'reminder' | 'poll' | 'product' | 'order'

export interface ComposerToolDef {
  id: ComposerToolId
  /** Nhãn ngắn (ô trong hộp Tiện ích, aria-label). */
  label: string
  /** Mô tả trong chế độ "Sắp xếp". */
  hint: string
  icon: LucideIcon
  /** Nhóm trong hộp Tiện ích. */
  group: 'send' | 'tool'
}

/** Mọi tiện ích có thể ghim. Thứ tự ở đây = thứ tự hiện trong hộp Tiện ích. Khớp backend `COMPOSER_TOOL_IDS`. */
export const COMPOSER_TOOLS: ComposerToolDef[] = [
  { id: 'image', get label() { return i18n.t('sharedMisc:composerTools.image.label') }, get hint() { return i18n.t('sharedMisc:composerTools.image.hint') }, icon: ImageIcon, group: 'send' },
  { id: 'file', get label() { return i18n.t('sharedMisc:composerTools.file.label') }, get hint() { return i18n.t('sharedMisc:composerTools.file.hint') }, icon: Paperclip, group: 'send' },
  { id: 'sticker', get label() { return i18n.t('sharedMisc:composerTools.sticker.label') }, get hint() { return i18n.t('sharedMisc:composerTools.sticker.hint') }, icon: Smile, group: 'send' },
  { id: 'library', get label() { return i18n.t('sharedMisc:composerTools.library.label') }, get hint() { return i18n.t('sharedMisc:composerTools.library.hint') }, icon: FolderOpen, group: 'send' },
  { id: 'quick', get label() { return i18n.t('sharedMisc:composerTools.quick.label') }, get hint() { return i18n.t('sharedMisc:composerTools.quick.hint') }, icon: Zap, group: 'send' },
  { id: 'product', get label() { return i18n.t('sharedMisc:composerTools.product.label') }, get hint() { return i18n.t('sharedMisc:composerTools.product.hint') }, icon: ShoppingBag, group: 'send' },
  { id: 'card', get label() { return i18n.t('sharedMisc:composerTools.card.label') }, get hint() { return i18n.t('sharedMisc:composerTools.card.hint') }, icon: IdCard, group: 'send' },
  { id: 'bank', get label() { return i18n.t('sharedMisc:composerTools.bank.label') }, get hint() { return i18n.t('sharedMisc:composerTools.bank.hint') }, icon: CreditCard, group: 'send' },
  { id: 'link', get label() { return i18n.t('sharedMisc:composerTools.link.label') }, get hint() { return i18n.t('sharedMisc:composerTools.link.hint') }, icon: Link2, group: 'send' },
  { id: 'order', get label() { return i18n.t('sharedMisc:composerTools.order.label') }, get hint() { return i18n.t('sharedMisc:composerTools.order.hint') }, icon: PackagePlus, group: 'tool' },
  { id: 'reminder', get label() { return i18n.t('sharedMisc:composerTools.reminder.label') }, get hint() { return i18n.t('sharedMisc:composerTools.reminder.hint') }, icon: BellRing, group: 'tool' },
  { id: 'poll', get label() { return i18n.t('sharedMisc:composerTools.poll.label') }, get hint() { return i18n.t('sharedMisc:composerTools.poll.hint') }, icon: BarChart3, group: 'tool' },
]

const BY_ID = new Map<string, ComposerToolDef>(COMPOSER_TOOLS.map((t) => [t.id, t]))
export const toolDef = (id: string): ComposerToolDef | undefined => BY_ID.get(id)
export const isToolId = (id: unknown): id is ComposerToolId => typeof id === 'string' && BY_ID.has(id)

/** Số tiện ích ghim tối đa — khớp backend `MAX_COMPOSER_PINNED`. */
export const MAX_PINNED = 5
/** Ghim sẵn khi người dùng chưa tuỳ chỉnh: ảnh · trả lời nhanh · sản phẩm. */
export const DEFAULT_PINNED: ComposerToolId[] = ['image', 'quick', 'product']

/**
 * Làm sạch danh sách đọc từ bộ nhớ / máy chủ: chỉ giữ mã hợp lệ, bỏ trùng, giữ thứ tự, cắt ở `MAX_PINNED`.
 * Không phải mảng (chưa từng tuỳ chỉnh) → mặc định; mảng RỖNG là lựa chọn hợp lệ ("không ghim gì") — khác với "chưa chọn".
 */
export function normalizeTools(raw: unknown): ComposerToolId[] {
  if (!Array.isArray(raw)) return [...DEFAULT_PINNED]
  const out: ComposerToolId[] = []
  for (const x of raw) if (isToolId(x) && !out.includes(x)) out.push(x)
  return out.slice(0, MAX_PINNED)
}

/** Ghim / bỏ ghim. Ghim khi đã đủ `MAX_PINNED` → không đổi (giao diện báo "tối đa"). Ghim thêm → cuối danh sách. */
export function toggleTool(list: ComposerToolId[], id: ComposerToolId): ComposerToolId[] {
  if (list.includes(id)) return list.filter((x) => x !== id)
  return list.length >= MAX_PINNED ? list : [...list, id]
}

/** Dời một tiện ích lên (-1) / xuống (+1) một bậc; ở mép hoặc chưa ghim → không đổi. */
export function moveTool(list: ComposerToolId[], id: ComposerToolId, dir: -1 | 1): ComposerToolId[] {
  const i = list.indexOf(id)
  const j = i + dir
  if (i < 0 || j < 0 || j >= list.length) return list
  const next = [...list]
  ;[next[i], next[j]] = [next[j], next[i]]
  return next
}

/** Thứ tự trong chế độ "Sắp xếp": đang ghim (theo thứ tự đã xếp) rồi tới các tiện ích còn lại (theo danh mục). */
export function editorOrder(list: ComposerToolId[]): ComposerToolId[] {
  return [...list, ...COMPOSER_TOOLS.map((t) => t.id).filter((id) => !list.includes(id))]
}

export const isDefaultPinned = (list: ComposerToolId[]) => list.length === DEFAULT_PINNED.length && list.every((x, i) => x === DEFAULT_PINNED[i])

// ── Bản lưu: bộ nhớ đệm trên máy + đồng bộ theo tài khoản ─────────────────────

/** Danh sách ghim + thời điểm người dùng sửa (ISO) — quyết định bản nào mới hơn giữa các thiết bị. */
export interface ToolsPref { tools: ComposerToolId[]; updatedAt: string }

export const toolsStorageKey = (userId?: string | null) => `ecdp_composer_tools:${userId || 'anon'}`

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const stampOf = (iso: unknown): number => (typeof iso === 'string' ? Date.parse(iso) : NaN)

/** Làm sạch bản từ máy chủ / bộ nhớ: phải có mảng + giờ đọc được; mã lạ bị loại (rỗng vẫn hợp lệ). Hỏng → null ("không có bản nào"). */
export function parseToolsPref(raw: unknown): ToolsPref | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const { tools, updatedAt } = raw as { tools?: unknown; updatedAt?: unknown }
  if (!Array.isArray(tools) || !Number.isFinite(stampOf(updatedAt))) return null
  return { tools: normalizeTools(tools), updatedAt: new Date(stampOf(updatedAt)).toISOString() }
}

export type ToolsMergeAction = 'none' | 'adopt-remote' | 'push-local'

export const sameTools = (a: ToolsPref, b: ToolsPref) => a.tools.length === b.tools.length && a.tools.every((x, i) => x === b.tools[i])

/**
 * Hợp nhất bản trên máy (`local`, null = chưa từng tuỳ chỉnh) với bản máy chủ (`remote`, null = máy chủ chưa có):
 * cả hai trống → không làm gì; máy chủ trống mà máy này có → đẩy lên; máy này trống → nhận bản máy chủ;
 * cả hai có → `updatedAt` mới hơn thắng; bằng nhau mà khác nội dung → theo máy chủ (một nguồn sự thật).
 */
export function mergeToolsPref(local: ToolsPref | null, remote: ToolsPref | null): { pref: ToolsPref | null; action: ToolsMergeAction } {
  if (!remote) return local ? { pref: local, action: 'push-local' } : { pref: null, action: 'none' }
  if (!local) return { pref: remote, action: 'adopt-remote' }
  const tl = stampOf(local.updatedAt)
  const tr = stampOf(remote.updatedAt)
  if (tr > tl) return { pref: remote, action: 'adopt-remote' }
  if (tl > tr) return { pref: local, action: 'push-local' }
  return sameTools(local, remote) ? { pref: local, action: 'none' } : { pref: remote, action: 'adopt-remote' }
}

export function loadToolsPref(userId: string | null | undefined, store?: KeyValueStore): ToolsPref | null {
  try {
    return parseToolsPref(JSON.parse((store ?? localStorage).getItem(toolsStorageKey(userId)) ?? 'null'))
  } catch {
    return null
  }
}

export function saveToolsPref(userId: string | null | undefined, pref: ToolsPref, store?: KeyValueStore): void {
  try { (store ?? localStorage).setItem(toolsStorageKey(userId), JSON.stringify(pref)) } catch { /* bộ nhớ bị chặn / đầy → vẫn dùng được trong phiên */ }
}
